import React, { useEffect, useState } from 'react';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from './ChartLayout';
import { ConsumptionBasedGHGInventory } from './ConsumptionBasedGHGInventory';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import { fetchConsumptionBasedGHGData, ConsumptionBasedGHGResult } from '../../services/consumptionBasedGHGService';

type ChartType = 'stacked-area' | 'line' | 'pie';

interface ConsumptionBasedGHGInventoryWithMetricsProps {
  location: string;
}

export const ConsumptionBasedGHGInventoryWithMetrics: React.FC<ConsumptionBasedGHGInventoryWithMetricsProps> = ({ location }) => {
  const [ghgData, setGhgData] = useState<ConsumptionBasedGHGResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartType, setChartType] = useState<ChartType>('stacked-area');
  const [activeView, setActiveView] = useState<string>('overview');

  useEffect(() => {
    const loadData = async () => {
      if (!location) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const result = await fetchConsumptionBasedGHGData(location);
        setGhgData(result);
      } catch (err) {
        console.error('Error loading consumption-based GHG data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load consumption-based GHG data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [location]);

  if (loading) {
    return (
      <ChartLayout
        title="Consumption-Based GHG Inventory"
        description="Loading consumption-based greenhouse gas emissions data..."
        chartType="line"
        keyMetrics={
          <KeyMetrics>
            <KeyMetric 
              value="Loading..." 
              label="Total consumption emissions (MT CO₂e per Community)"
              color="#1e3a8a"
            />
            <KeyMetric 
              value="Loading..." 
              label="Change since 2007"
              color="#10b981"
            />
            <KeyMetric 
              value="Loading..." 
              label="Transportation consumption share"
              color="#f59e0b"
            />
          </KeyMetrics>
        }
        chartContent={<div style={{ textAlign: 'center', padding: '40px' }}>Loading consumption-based GHG data...</div>}
        overviewContent={
          <OverviewContent
            title="Consumption-Based GHG Inventory Overview"
            description="Loading consumption-based greenhouse gas emissions data..."
          >
            <p>Loading data for {location}...</p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Consumption-based inventories reveal the full climate impact of lifestyle choices and support behavioral change strategies."
          >
            <div className="methodology-details-simple">
              <h5>Behavioral Change</h5>
              <p>Understanding consumption patterns helps develop strategies to encourage sustainable choices and reduce emissions.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Lifestyle Impact</h5>
              <p>Consumption-based data reveals how individual and community choices contribute to climate change.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Consumption patterns vary significantly by income and access, requiring equitable approaches to sustainable consumption."
          >
            <div className="methodology-details-simple">
              <h5>Access to Alternatives</h5>
              <p>Low-income communities may have limited access to sustainable consumption options and clean alternatives.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Affordability</h5>
              <p>Sustainable consumption must be affordable and accessible to all income levels to ensure equitable climate action.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Consumption-based GHG inventories allocate emissions to final consumers based on consumption patterns and economic data."
            dataSources={[
              'Consumer Expenditures Survey (CE)',
              'National Household Travel Survey (NHTS)',
              'Residential Energy Consumption Survey (RECS)',
              'US EPA USEEIO Model',
              'US EPA eGrid emission factors'
            ]}
            methodology={[
              'Model estimates household consumption and associated emissions using multiple linear regression.',
              'Emissions calculated using US EPA USEEIO Model for consumption-to-emissions mapping.',
              'Electricity emissions use EPA eGrid region emission factors scaled to local geography.',
              'Transportation emissions based on vehicle miles traveled and fuel economy data.',
              'Model results scaled to real-world data at state/local level for accuracy.',
              'Data represents 2023 estimates of average household carbon footprint by census tract.',
              'Values converted from grams to metric tons CO₂ equivalent and multiplied by household count for community totals.',
              'Methodology accounts for all lifecycle emissions including production, transport, and consumption.',
              'Limitations: assumes imports have same carbon intensity as domestic production, excludes government emissions.'
            ]}
          />
        }
        activeView={activeView}
        onViewChange={setActiveView}
        showRelevanceEquity={true}
        location={location}
        overviewSectionId="ghg_inventory"
        overviewChartId="consumption_based_ghg"
      />
    );
  }

  if (error) {
    return (
      <ChartLayout
        title="Consumption-Based GHG Inventory"
        description="Error loading consumption-based greenhouse gas emissions data."
        chartType="line"
        keyMetrics={
          <KeyMetrics>
            <KeyMetric 
              value="Error" 
              label="Total consumption emissions (MT CO₂e per Community)"
              color="#ef4444"
            />
            <KeyMetric 
              value="Error" 
              label="Change since 2007"
              color="#ef4444"
            />
            <KeyMetric 
              value="Error" 
              label="Transportation consumption share"
              color="#ef4444"
            />
          </KeyMetrics>
        }
        chartContent={<div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>Error: {error}</div>}
        overviewContent={
          <OverviewContent
            title="Consumption-Based GHG Inventory Overview"
            description="Error loading consumption-based greenhouse gas emissions data."
          >
            <p>Error loading data for {location}: {error}</p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Consumption-based inventories reveal the full climate impact of lifestyle choices and support behavioral change strategies."
          >
            <div className="methodology-details-simple">
              <h5>Behavioral Change</h5>
              <p>Understanding consumption patterns helps develop strategies to encourage sustainable choices and reduce emissions.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Lifestyle Impact</h5>
              <p>Consumption-based data reveals how individual and community choices contribute to climate change.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Consumption patterns vary significantly by income and access, requiring equitable approaches to sustainable consumption."
          >
            <div className="methodology-details-simple">
              <h5>Access to Alternatives</h5>
              <p>Low-income communities may have limited access to sustainable consumption options and clean alternatives.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Affordability</h5>
              <p>Sustainable consumption must be affordable and accessible to all income levels to ensure equitable climate action.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Consumption-based GHG inventories allocate emissions to final consumers based on consumption patterns and economic data."
            dataSources={[
              'Consumer Expenditures Survey (CE)',
              'National Household Travel Survey (NHTS)',
              'Residential Energy Consumption Survey (RECS)',
              'US EPA USEEIO Model',
              'US EPA eGrid emission factors'
            ]}
            methodology={[
              'Model estimates household consumption and associated emissions using multiple linear regression.',
              'Emissions calculated using US EPA USEEIO Model for consumption-to-emissions mapping.',
              'Electricity emissions use EPA eGrid region emission factors scaled to local geography.',
              'Transportation emissions based on vehicle miles traveled and fuel economy data.',
              'Model results scaled to real-world data at state/local level for accuracy.',
              'Data represents 2023 estimates of average household carbon footprint by census tract.',
              'Values converted from grams to metric tons CO₂ equivalent and multiplied by household count for community totals.',
              'Methodology accounts for all lifecycle emissions including production, transport, and consumption.',
              'Limitations: assumes imports have same carbon intensity as domestic production, excludes government emissions.'
            ]}
          />
        }
        activeView={activeView}
        onViewChange={setActiveView}
        showRelevanceEquity={true}
        location={location}
        overviewSectionId="ghg_inventory"
        overviewChartId="consumption_based_ghg"
      />
    );
  }

  if (!ghgData) {
    return (
      <ChartLayout
        title="Consumption-Based GHG Inventory"
        description="No consumption-based greenhouse gas emissions data available."
        chartType="line"
        keyMetrics={
          <KeyMetrics>
            <KeyMetric 
              value="No data" 
              label="Total consumption emissions (MT CO₂e per Community)"
              color="#6b7280"
            />
            <KeyMetric 
              value="No data" 
              label="Change since 2007"
              color="#6b7280"
            />
            <KeyMetric 
              value="No data" 
              label="Transportation consumption share"
              color="#6b7280"
            />
          </KeyMetrics>
        }
        chartContent={<div style={{ textAlign: 'center', padding: '40px' }}>No consumption-based GHG data available for {location}</div>}
        overviewContent={
          <OverviewContent
            title="Consumption-Based GHG Inventory Overview"
            description="No consumption-based greenhouse gas emissions data available."
          >
            <p>No consumption-based GHG data available for {location}.</p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Consumption-based inventories reveal the full climate impact of lifestyle choices and support behavioral change strategies."
          >
            <div className="methodology-details-simple">
              <h5>Behavioral Change</h5>
              <p>Understanding consumption patterns helps develop strategies to encourage sustainable choices and reduce emissions.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Lifestyle Impact</h5>
              <p>Consumption-based data reveals how individual and community choices contribute to climate change.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Consumption patterns vary significantly by income and access, requiring equitable approaches to sustainable consumption."
          >
            <div className="methodology-details-simple">
              <h5>Access to Alternatives</h5>
              <p>Low-income communities may have limited access to sustainable consumption options and clean alternatives.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Affordability</h5>
              <p>Sustainable consumption must be affordable and accessible to all income levels to ensure equitable climate action.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Consumption-based GHG inventories allocate emissions to final consumers based on consumption patterns and economic data."
            dataSources={[
              'Consumer Expenditures Survey (CE)',
              'National Household Travel Survey (NHTS)',
              'Residential Energy Consumption Survey (RECS)',
              'US EPA USEEIO Model',
              'US EPA eGrid emission factors'
            ]}
            methodology={[
              'Model estimates household consumption and associated emissions using multiple linear regression.',
              'Emissions calculated using US EPA USEEIO Model for consumption-to-emissions mapping.',
              'Electricity emissions use EPA eGrid region emission factors scaled to local geography.',
              'Transportation emissions based on vehicle miles traveled and fuel economy data.',
              'Model results scaled to real-world data at state/local level for accuracy.',
              'Data represents 2023 estimates of average household carbon footprint by census tract.',
              'Values converted from grams to metric tons CO₂ equivalent and multiplied by household count for community totals.',
              'Methodology accounts for all lifecycle emissions including production, transport, and consumption.',
              'Limitations: assumes imports have same carbon intensity as domestic production, excludes government emissions.'
            ]}
          />
        }
        activeView={activeView}
        onViewChange={setActiveView}
        showRelevanceEquity={true}
        location={location}
        overviewSectionId="ghg_inventory"
        overviewChartId="consumption_based_ghg"
      />
    );
  }

  // Format the metrics for display
  const formatValue = (value: number, decimals: number = 1) => {
    return value.toFixed(decimals);
  };

  const formatPercentage = (value: number, decimals: number = 1) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}%`;
  };

  return (
    <ChartLayout
      title="Consumption-Based GHG Inventory"
      description="Greenhouse gas emissions based on consumption patterns and lifestyle choices (2007-2023)."
      chartType="line"
      chartSelector={{
        options: [
          { value: 'stacked-area', label: 'Stacked Area' },
          { value: 'line', label: 'Line Chart' },
          { value: 'pie', label: 'Pie Chart (2023)' }
        ],
        value: chartType,
        onChange: (value) => setChartType(value as ChartType)
      }}
      keyMetrics={
        <KeyMetrics>
          <KeyMetric 
            value={`${formatValue(ghgData.totalEmissions)}`}
            label="Total consumption emissions (MT CO₂e per Community)"
            color="#1e3a8a"
          />
          <KeyMetric 
            value={formatPercentage(ghgData.percentChange)}
            label="Change since 2007"
            color={ghgData.percentChange < 0 ? "#10b981" : "#ef4444"}
          />
          <KeyMetric 
            value={`${formatValue(ghgData.transportationShare)}%`}
            label="Transportation consumption share"
            color="#f59e0b"
          />
        </KeyMetrics>
      }
      chartContent={<ConsumptionBasedGHGInventory location={location} chartType={chartType} setChartType={setChartType} />}
      overviewContent={
        <OverviewContent
          title="Consumption-Based GHG Inventory Overview"
          description="This chart displays greenhouse gas emissions based on consumption patterns, revealing the climate impact of lifestyle choices and consumption decisions."
        >
          <p>
            {location} has consumption-based emissions of {formatValue(ghgData.totalEmissions)} metric tons CO₂ equivalent per community in {ghgData.latestYear}, 
            representing a {formatPercentage(ghgData.percentChange)} change since 2007. 
            Transportation consumption accounts for {formatValue(ghgData.transportationShare)}% of total emissions, 
            followed by housing ({formatValue(ghgData.housingPercent)}%), food ({formatValue(ghgData.foodPercent)}%), 
            goods ({formatValue(ghgData.goodsPercent)}%), and services ({formatValue(ghgData.servicesPercent)}%). 
            This approach helps identify opportunities for behavioral change and sustainable consumption patterns.
          </p>
          <p>
            Compared to the California average of {formatValue(ghgData.californiaAverage)} MT CO₂e per community, 
            {location} is at {formatValue(ghgData.locationVsCalifornia)}% of the state average.
          </p>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Relevance to Climate Action"
          description="Consumption-based inventories reveal the full climate impact of lifestyle choices and support behavioral change strategies."
        >
          <div className="methodology-details-simple">
            <h5>Behavioral Change</h5>
            <p>Understanding consumption patterns helps develop strategies to encourage sustainable choices and reduce emissions.</p>
          </div>
          <div className="methodology-details-simple">
            <h5>Lifestyle Impact</h5>
            <p>Consumption-based data reveals how individual and community choices contribute to climate change.</p>
          </div>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent
          title="Equity Considerations"
          description="Consumption patterns vary significantly by income and access, requiring equitable approaches to sustainable consumption."
        >
          <div className="methodology-details-simple">
            <h5>Access to Alternatives</h5>
            <p>Low-income communities may have limited access to sustainable consumption options and clean alternatives.</p>
          </div>
          <div className="methodology-details-simple">
            <h5>Affordability</h5>
            <p>Sustainable consumption must be affordable and accessible to all income levels to ensure equitable climate action.</p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Methodology"
          description="Consumption-based GHG inventories allocate emissions to final consumers based on consumption patterns and economic data."
          dataSources={[
            'Consumer Expenditures Survey (CE)',
            'National Household Travel Survey (NHTS)',
            'Residential Energy Consumption Survey (RECS)',
            'US EPA USEEIO Model',
            'US EPA eGrid emission factors'
          ]}
          methodology={[
            'Model estimates household consumption and associated emissions using multiple linear regression.',
            'Emissions calculated using US EPA USEEIO Model for consumption-to-emissions mapping.',
            'Electricity emissions use EPA eGrid region emission factors scaled to local geography.',
            'Transportation emissions based on vehicle miles traveled and fuel economy data.',
            'Model results scaled to real-world data at state/local level for accuracy.',
            'Data represents 2022 estimates of average household carbon footprint by census tract.',
            'Values converted from grams to metric tons CO₂ equivalent per household.',
            'Methodology accounts for all lifecycle emissions including production, transport, and consumption.',
            'Limitations: assumes imports have same carbon intensity as domestic production, excludes government emissions.'
          ]}
        />
      }
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
      location={location}
      overviewSectionId="ghg_inventory"
      overviewChartId="consumption_based_ghg"
    />
  );
};
